/**
 * Reading a refused session save.
 *
 * The server enforces the saved-DOCUMENT cap (design section 3.5.6) and
 * answers with 403 plus X-Error-Code and X-Error-Message. Both headers are
 * exposed to cross-origin JavaScript by CorsConfig - verified against QA, not
 * assumed - which is what lets the UI show the server's own sentence instead of
 * inventing one.
 */

/**
 * Why the save was refused. The two need different treatment:
 *
 * - `limit`  the library WORKS and is full. A capacity statement (section
 *            3.3.2): factual sentence, concrete way forward, no upsell tone.
 * - `unavailable` the library is not part of this session at all. That is the
 *            artifact-gate case, and it uses the section 3.5.3 copy.
 */
export type SessionRefusalKind = 'limit' | 'unavailable';

export interface SessionRefusal {
  kind: SessionRefusalKind;
  /** The server's own wording. Never rewritten here. */
  message: string;
  code: string;
}

const UNAVAILABLE_CODES = ['SESSION_LIBRARY_UNAVAILABLE', 'NO_CUSTOMER_CONTEXT'];
const LIMIT_CODES = ['SAVED_DOCUMENT_LIMIT_REACHED'];

/**
 * Classify a failed save, or return null if this is an ordinary error that the
 * caller's existing handling should deal with.
 *
 * Deliberately narrow: only the codes this module knows are claimed. A 500 or a
 * dropped connection is not a capability problem and must not be dressed as one
 * - telling someone to buy a licence because the database blinked would be a
 * lie that costs trust.
 */
export function readSessionRefusal(status: number, headers: Headers): SessionRefusal | null {
  if (status !== 403) {
    return null;
  }

  const code = headers.get('X-Error-Code') || '';
  const message = headers.get('X-Error-Message') || '';

  if (LIMIT_CODES.includes(code)) {
    return {
      kind: 'limit',
      message: message || 'This session has used all of its saved documents.',
      code,
    };
  }
  if (UNAVAILABLE_CODES.includes(code)) {
    return {
      kind: 'unavailable',
      message: message || 'Saving is not available for this session.',
      code,
    };
  }
  return null;
}

/**
 * Thrown once a refusal has already been shown to the user.
 *
 * Save calls throw on failure and their callers show an error notification. A
 * refusal is not an error - it has its own prompt with a way forward - so the
 * caller must stay quiet rather than stack a red "Failed to save session" on
 * top of it.
 */
export class SessionRefusalShown extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionRefusalShown';
  }
}
